/**
 * Admin › World Changer Nominations — review and approve/reject public
 * submissions from the public /world-changers nomination form.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, X, Trash2, ExternalLink, RefreshCw } from "lucide-react";

export default function AdminWorldChangerNominations() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/world-changer-nominations", { params: { status: statusFilter } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const approve = async (n) => {
    try {
      const { data } = await http.post(`/admin/world-changer-nominations/${n.id}/approve`);
      toast.success(`Promoted "${n.name}" → /world-changers/${data.slug}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const reject = async (n) => {
    const reason = window.prompt(`Reject reason (optional) for "${n.name}":`);
    try {
      await http.post(`/admin/world-changer-nominations/${n.id}/reject`, null, { params: reason ? { reason } : {} });
      toast.success("Nomination rejected");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  const del = async (n) => {
    if (!window.confirm(`Permanently delete nomination "${n.name}"?`)) return;
    try {
      await http.delete(`/admin/world-changer-nominations/${n.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const statuses = [
    { v: "pending", label: "Pending" },
    { v: "approved", label: "Approved" },
    { v: "rejected", label: "Rejected" },
    { v: "all", label: "All" },
  ];

  return (
    <div data-testid="admin-wc-nominations-panel" className="mt-10">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline mb-1">NOMINATIONS · WORLD CHANGERS</div>
          <h3 className="font-display text-2xl">Public nominations for the directory.</h3>
        </div>
        <button onClick={reload} className="wofy-btn-ghost text-xs">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-6">
        {statuses.map((s) => (
          <button
            key={s.v}
            data-testid={`wcn-filter-${s.v}`}
            onClick={() => setStatusFilter(s.v)}
            className={`px-3 py-1.5 text-xs rounded-full transition-colors ${statusFilter === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={statusFilter === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="wofy-card text-center py-12">
          <div className="font-display text-xl">No {statusFilter === "all" ? "" : statusFilter} nominations.</div>
        </div>
      )}
      <div className="grid gap-3">
        {items.map((n) => (
          <div
            key={n.id}
            data-testid={`wcn-row-${n.id}`}
            className="wofy-card flex flex-wrap items-start gap-5"
          >
            <div className="flex-1 min-w-[260px]">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${n.status === "pending" ? "" : n.status === "approved" ? "" : ""}`}
                  style={{
                    background: n.status === "pending" ? "#FEF3C7" : n.status === "approved" ? "#D1FAE5" : "#FEE2E2",
                    color: n.status === "pending" ? "#92400E" : n.status === "approved" ? "#065F46" : "#991B1B",
                  }}
                >
                  {n.status}
                </span>
                <span className="text-[11px] font-mono opacity-60">{n.created_at?.slice(0, 10)}</span>
              </div>
              <div className="font-display text-lg">{n.name}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
                {[n.nationality, n.occupation].filter(Boolean).join(" · ") || "—"}
              </div>
              {n.tagline && (
                <p className="mt-2 text-sm italic" style={{ color: "var(--wofy-text-soft)" }}>{n.tagline}</p>
              )}
              {n.reason && (
                <p className="mt-2 text-sm leading-relaxed line-clamp-4">{n.reason}</p>
              )}
              {n.categories?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {n.categories.map((c) => (
                    <span key={c} className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }}>{c}</span>
                  ))}
                </div>
              )}
              <div className="mt-2 text-[11px] font-mono opacity-60">
                by {n.submitter_name || n.submitter_email || "anonymous"}
                {n.rejection_reason ? ` · rejected: ${n.rejection_reason}` : ""}
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs">
                {n.wiki_url && (
                  <a href={n.wiki_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1" style={{ color: "var(--wofy-primary)" }}>
                    Wikipedia <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {n.reference_url && (
                  <a href={n.reference_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1" style={{ color: "var(--wofy-primary)" }}>
                    Reference <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {n.status === "pending" && (
                <>
                  <button
                    onClick={() => approve(n)}
                    data-testid={`wcn-approve-${n.id}`}
                    className="wofy-btn-primary text-xs"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => reject(n)}
                    data-testid={`wcn-reject-${n.id}`}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "#B23A3A" }}
                  >
                    <X className="w-4 h-4" /> Reject
                  </button>
                </>
              )}
              <button
                onClick={() => del(n)}
                data-testid={`wcn-delete-${n.id}`}
                className="wofy-btn-ghost text-xs"
                style={{ color: "#B23A3A" }}
                aria-label="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
